import { useEffect, useRef, useState } from "react";
import {
  AnimatePresence,
  motion,
  useInView,
  useReducedMotion,
  useScroll,
  useTransform,
} from "framer-motion";
import Lenis from "lenis";
import { ArrowRight, ArrowUpRight, Check, ChevronDown, Menu, X } from "lucide-react";
import FlowPage from "@/FlowPage";
import "@/App.css";

const COLOR_LOGO_URL =
  "https://customer-assets-wrfwihn1.emergentagent.net/job_db200477-48fb-4087-94b1-eaecfe8c6dd7/artifacts/frvkftqj_Asset%2010%404x-8.webp";
const WHITE_LOGO_URL =
  "https://customer-assets-wrfwihn1.emergentagent.net/job_db200477-48fb-4087-94b1-eaecfe8c6dd7/artifacts/wrc1eu0r_Asset%2022%404x-8.webp";
const GRID_IMAGE_URL =
  "https://static.prod-images.emergentagent.com/jobs/db200477-48fb-4087-94b1-eaecfe8c6dd7/images/b50c7cd1ffc7d4c9c67638c5038088ddb78227612865d9a0bb8f9bb98b57c0d0.jpeg";
const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.16, 1, 0.3, 1];

const products = [
  {
    slug: "ems",
    name: "Suvera EMS",
    category: "Energy Monitoring System",
    description: "See where energy goes, understand usage, and improve operational efficiency.",
    accent: "#119F4A",
    layout: "wide",
  },
  {
    slug: "weighguard",
    name: "WeighGuard",
    category: "Weighbridge Management System",
    description: "Digitise weighing operations with secure, traceable transaction workflows.",
    accent: "#197EBD",
    layout: "tall",
  },
  {
    slug: "bandobast-ai",
    name: "Bandobast AI",
    category: "AI-Powered Camera Intelligence",
    description: "Your cameras see. AI understands. Turn visual infrastructure into action.",
    accent: "#ED1C24",
    layout: "tall",
  },
  {
    slug: "dms",
    name: "Suvera DMS",
    category: "Document Management System",
    description: "Bring documents, permissions, versions, and workflows into one clear system.",
    accent: "#197EBD",
    layout: "wide",
  },
  {
    slug: "flow",
    name: "Suvera Flow",
    category: "Intelligent Workflow & Visitor Management",
    description: "Move approvals, visitors, notifications, and workplace workflows forward.",
    accent: "#F2AD21",
    layout: "wide",
  },
  {
    slug: "ai",
    name: "AI Capabilities",
    category: "Artificial Intelligence",
    description: "Add computer vision, automation, document intelligence, and analytics where it matters.",
    accent: "#F2AD21",
    layout: "tall",
  },
];

const solutions = [
  ["Digital Transformation", "Modernise manual and legacy operations.", "#197EBD"],
  ["Intelligent Automation", "Reduce repetitive work and connect workflows.", "#F2AD21"],
  ["Computer Vision", "Transform cameras into intelligent visual systems.", "#ED1C24"],
  ["Enterprise Software", "Build secure and scalable business platforms.", "#1A2F5A"],
  ["Data & Analytics", "Turn operational data into actionable insight.", "#119F4A"],
  ["System Integration", "Connect applications, devices, APIs, and infrastructure.", "#197EBD"],
];

const chapters = [
  ["01", "Problem first", "We begin with the business problem, not the technology."],
  ["02", "Product thinking", "We design complete digital products instead of disconnected features."],
  ["03", "AI where it matters", "We apply AI where it creates meaningful operational value."],
  ["04", "Built for reality", "We design for real users, infrastructure, data, and business environments."],
  ["05", "Connected systems", "We connect software, devices, data, people, and workflows."],
];

const industries = [
  ["Industrial", "Energy, weighing, monitoring, automation, and intelligence."],
  ["Infrastructure", "Facilities, assets, energy, and security intelligence."],
  ["Logistics", "Vehicles, weighbridges, transactions, and operational visibility."],
  ["Corporate", "Workplace workflows, visitors, documents, and operations."],
  ["Manufacturing", "Energy management, computer vision, and automation."],
  ["Enterprise", "Connected digital systems for complex organisations."],
];

function Reveal({ children, className = "", delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.16 });
  const reduceMotion = useReducedMotion();
  const hidden = { opacity: 0, y: reduceMotion ? 0 : 24 };
  return (
    <motion.div
      ref={ref}
      className={className}
      initial={hidden}
      animate={inView ? { opacity: 1, y: 0 } : hidden}
      transition={{ duration: 0.72, delay, ease: EASE }}
    >
      {children}
    </motion.div>
  );
}

function SmoothScroll() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true, autoRaf: false });
    let frame;
    const raf = (time) => {
      lenis.raf(time);
      frame = window.requestAnimationFrame(raf);
    };
    frame = window.requestAnimationFrame(raf);
    return () => {
      window.cancelAnimationFrame(frame);
      lenis.destroy();
    };
  }, []);
  return null;
}

function Logo({ variant = "white", testId }) {
  return (
    <img
      className={`brand-logo brand-logo--${variant}`}
      src={variant === "white" ? WHITE_LOGO_URL : COLOR_LOGO_URL}
      alt="Suvera Technology"
      data-testid={testId}
    />
  );
}

function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const closeMenus = () => {
    setProductsOpen(false);
    setMobileOpen(false);
  };

  return (
    <header className={`site-header ${scrolled ? "is-scrolled" : ""}`} data-testid="site-header">
      <div className="header-inner container">
        <a className="header-logo-link" href="#top" onClick={closeMenus} data-testid="header-logo-link">
          <Logo variant="color" testId="header-logo" />
        </a>
        <nav className="desktop-nav" aria-label="Primary navigation">
          <div className="nav-products-group">
            <button
              className="nav-link nav-products-trigger"
              type="button"
              aria-expanded={productsOpen}
              onClick={() => setProductsOpen((open) => !open)}
              data-testid="nav-products-btn"
            >
              Products <ChevronDown size={15} aria-hidden="true" />
            </button>
            <AnimatePresence>
              {productsOpen && (
                <motion.div
                  className="products-dropdown"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.2, ease: EASE }}
                >
                  {products.map((product) => (
                    <a
                      key={product.slug}
                      href={product.slug === "flow" ? "/products/flow" : "#products"}
                      onClick={closeMenus}
                      data-testid={`dropdown-${product.slug}-link`}
                    >
                      <span className="dropdown-dot" style={{ backgroundColor: product.accent }} />
                      <span>
                        <strong>{product.name}</strong>
                        <small>{product.category}</small>
                      </span>
                      <ArrowUpRight size={15} aria-hidden="true" />
                    </a>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <a className="nav-link" href="#suite-overview" data-testid="nav-suite-link">Suite</a>
          <a className="nav-link" href="#ai" data-testid="nav-ai-link">AI</a>
          <a className="nav-link" href="#solutions" data-testid="nav-solutions-link">Solutions</a>
          <a className="nav-link" href="#company" data-testid="nav-company-link">Company</a>
          <a className="nav-link" href="#contact" data-testid="nav-contact-link">Contact</a>
        </nav>
        <div className="header-actions">
          <a className="header-talk-link" href="#contact" data-testid="nav-talk-link">
            Let&apos;s talk <ArrowUpRight size={16} aria-hidden="true" />
          </a>
          <button
            className="mobile-menu-button"
            type="button"
            aria-expanded={mobileOpen}
            aria-label={mobileOpen ? "Close navigation" : "Open navigation"}
            onClick={() => setMobileOpen((open) => !open)}
            data-testid="mobile-menu-btn"
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            className="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25, ease: EASE }}
          >
            <div className="mobile-menu-inner container">
              <a href="#products" onClick={closeMenus} data-testid="mobile-products-link">Products</a>
              <a href="#ai" onClick={closeMenus} data-testid="mobile-ai-link">AI</a>
              <a href="#solutions" onClick={closeMenus} data-testid="mobile-solutions-link">Solutions</a>
              <a href="#company" onClick={closeMenus} data-testid="mobile-company-link">Company</a>
              <a href="#contact" onClick={closeMenus} data-testid="mobile-contact-link">Contact</a>
              <a className="mobile-menu-cta" href="#contact" onClick={closeMenus} data-testid="mobile-talk-link">
                Start a conversation <ArrowUpRight size={17} aria-hidden="true" />
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

function SignatureLine({ dark = false }) {
  return (
    <div className={`signature-line ${dark ? "signature-line--dark" : ""}`} data-testid={dark ? "dark-signature-line" : "signature-line"}>
      <span>DATA</span>
      <div className="signature-line-track" aria-hidden="true">
        <i className="signal-point signal-point--red" />
        <i className="signal-point signal-point--green" />
        <i className="signal-point signal-point--blue" />
        <i className="signal-point signal-point--yellow" />
      </div>
      <span>INTELLIGENCE</span>
      <div className="signature-line-track signature-line-track--short" aria-hidden="true">
        <i className="signal-pulse" />
      </div>
      <span>ACTION</span>
    </div>
  );
}

function Hero() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const visualY = useTransform(scrollYProgress, [0, 1], [0, -56]);
  const cardLeftY = useTransform(scrollYProgress, [0, 1], [0, -28]);
  const cardRightY = useTransform(scrollYProgress, [0, 1], [0, -72]);
  const lines = ["Engineering", "intelligent", "digital products,", "powered by", "AI."];

  return (
    <section className="hero-section" id="top" ref={heroRef} data-testid="hero-section">
      <div className="container hero-layout">
        <div className="hero-copy">
          <Reveal className="eyebrow-row">
            <span className="eyebrow-mark" />
            <span data-testid="hero-eyebrow">Suvera Technology / Digital Product Engineering</span>
          </Reveal>
          <h1 className="hero-title" data-testid="hero-title" aria-label="Engineering intelligent digital products, powered by AI.">
            {lines.map((line, index) => (
              <span className="hero-line-mask" key={line}>
                <motion.span
                  className="hero-title-line"
                  initial={{ y: "110%", opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.85, delay: 0.2 + index * 0.12, ease: EASE }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>
          <Reveal className="hero-lead" delay={0.72}>
            <p>
              We build intelligent software systems that connect operations, data, infrastructure, and people — helping businesses work smarter, faster, and more efficiently.
            </p>
          </Reveal>
          <Reveal className="hero-actions" delay={0.84}>
            <a className="button button--light" href="#products" data-testid="hero-products-link">
              Explore our products <ArrowRight size={18} aria-hidden="true" />
            </a>
            <a className="button button--outline-light" href="#contact" data-testid="hero-contact-link">
              Talk to us <ArrowUpRight size={17} aria-hidden="true" />
            </a>
          </Reveal>
          <Reveal className="hero-capability-row" delay={0.96}>
            <span data-testid="hero-capability-ai">AI &amp; computer vision</span>
            <span data-testid="hero-capability-software">Enterprise software</span>
            <span data-testid="hero-capability-automation">Automation</span>
            <span data-testid="hero-capability-products">Digital products</span>
          </Reveal>
        </div>
        <Reveal className="hero-visual-column" delay={0.28}>
          <motion.figure className="hero-visual" style={{ y: visualY }} data-testid="hero-visual">
            <div className="hero-visual-shell">
              <img src={GRID_IMAGE_URL} alt="Abstract Suvera intelligence grid connecting operational data to action" />
              <div className="hero-visual-wash" aria-hidden="true" />
              <div className="hero-core-label">
                <span className="mono-label">Core layer</span>
                <strong>SUVERA<br />INTELLIGENCE</strong>
                <span className="core-status"><i /> Orchestrating systems</span>
              </div>
              <div className="hero-orbit-label hero-orbit-label--energy">ENERGY</div>
              <div className="hero-orbit-label hero-orbit-label--vision">CAMERA</div>
              <div className="hero-orbit-label hero-orbit-label--docs">DOCUMENTS</div>
              <div className="hero-orbit-label hero-orbit-label--flow">WORKFLOWS</div>
              <motion.aside className="hero-floating-card hero-floating-card--ems" style={{ y: cardLeftY }} data-testid="hero-floating-card-ems">
                <span className="floating-card-label"><i className="status-dot status-dot--green" /> EMS / telemetry</span>
                <strong>Energy in view</strong>
                <small>Operational signal / active</small>
              </motion.aside>
              <motion.aside className="hero-floating-card hero-floating-card--vision" style={{ y: cardRightY }} data-testid="hero-floating-card-vision">
                <span className="floating-card-label"><i className="status-dot status-dot--red" /> Bandobast AI</span>
                <strong>See what matters</strong>
                <small>Vision intelligence / ready</small>
              </motion.aside>
              <motion.aside className="hero-floating-card hero-floating-card--weigh" style={{ y: cardLeftY }} data-testid="hero-floating-card-weigh">
                <span className="floating-card-label"><i className="status-dot status-dot--blue" /> WeighGuard</span>
                <strong>Operations connected</strong>
                <small>Traceable workflow / live</small>
              </motion.aside>
            </div>
            <figcaption className="hero-visual-caption">
              <span>Operational inputs</span>
              <SignatureLine dark />
              <span>Business outcomes</span>
            </figcaption>
          </motion.figure>
        </Reveal>
      </div>
    </section>
  );
}

function HeroEditorial() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const reduceMotion = useReducedMotion();
  const atlasY = useTransform(scrollYProgress, [0, 1], [0, -58]);
  const atlasScale = useTransform(scrollYProgress, [0, 1], [1, 1.035]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const suiteProducts = [
    { id: "ems", shortName: "EMS", name: "Suvera EMS", category: "Energy", accent: "#119F4A", left: 3, top: 8, point: [22, 22], path: "M 22 22 C 31 28 39 38 50 50", blurb: "Energy visibility and efficiency, connected to the wider operation." },
    { id: "weighguard", shortName: "WEIGHGUARD", name: "WeighGuard", category: "Weighbridge", accent: "#197EBD", left: 3, top: 41, point: [22, 50], path: "M 22 50 C 33 50 40 50 50 50", blurb: "Secure, traceable weighing workflows for physical operations." },
    { id: "bandobast-ai", shortName: "BANDOBAST AI", name: "Bandobast AI", category: "Computer vision", accent: "#ED1C24", left: 3, top: 74, point: [22, 78], path: "M 22 78 C 31 72 39 62 50 50", blurb: "Visual intelligence that turns camera infrastructure into action." },
    { id: "dms", shortName: "DMS", name: "Suvera DMS", category: "Documents", accent: "#197EBD", left: 73, top: 8, point: [78, 22], path: "M 78 22 C 69 28 61 38 50 50", blurb: "Documents, permissions, and workflows brought into one clear system." },
    { id: "flow", shortName: "FLOW", name: "Suvera Flow", category: "Workflows", accent: "#F2AD21", left: 73, top: 41, point: [78, 50], path: "M 78 50 C 67 50 60 50 50 50", blurb: "Visitors, approvals, notifications, and workplace workflows in motion." },
    { id: "ai", shortName: "AI", name: "AI Capabilities", category: "Intelligence", accent: "#F2AD21", left: 73, top: 74, point: [78, 78], path: "M 78 78 C 69 72 61 62 50 50", blurb: "Computer vision, automation, document intelligence, and analytics." },
  ];
  const activeProduct = suiteProducts.find((product) => product.id === selectedProduct);
  const lines = ["One intelligence", "platform.", "Every operation", "connected."];

  return (
    <section className="hero-editorial hero-suite" id="top" ref={heroRef} data-testid="hero-section">
      <div id="suite-overview" className="hero-suite-anchor" aria-hidden="true" />
      <div className="container hero-suite-layout">
        <div className="hero-suite-copy">
          <Reveal className="hero-editorial-meta">
            <span className="hero-editorial-index">01</span>
            <span data-testid="hero-eyebrow">Suvera Intelligence Platform / Connected suite</span>
          </Reveal>
          <h1 className="hero-editorial-title hero-suite-title" data-testid="hero-title" aria-label="One intelligence platform. Every operation connected.">
            {lines.map((line, index) => (
              <span className="hero-editorial-line-mask" key={line}>
                <motion.span className="hero-editorial-line" initial={{ y: "110%", opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.82, delay: 0.18 + index * 0.1, ease: EASE }}>
                  {index === 1 || index === 3 ? <em>{line}</em> : line}
                </motion.span>
              </span>
            ))}
          </h1>
          <Reveal className="hero-suite-tagline" delay={0.52}><span>Engineering intelligent digital products, powered by AI.</span></Reveal>
          <Reveal className="hero-editorial-lead hero-suite-lead" delay={0.64}><p data-testid="hero-lead">Suvera Technology builds software and AI systems that connect operations, data, infrastructure, and people — helping businesses work smarter, faster, and more efficiently.</p></Reveal>
          <Reveal className="hero-editorial-actions" delay={0.76}>
            <a className="button button--navy" href="#suite-overview" data-testid="hero-suite-link">Explore the connected suite <ArrowRight size={18} aria-hidden="true" /></a>
            <a className="button button--outline-navy" href="#contact" data-testid="hero-contact-link">Start a conversation <ArrowUpRight size={17} aria-hidden="true" /></a>
          </Reveal>
          <Reveal className="hero-foundation-strip" delay={0.88}>
            <span className="hero-foundation-label">Shared operating foundation</span>
            <span data-testid="hero-foundation-data">Unified data</span>
            <span data-testid="hero-foundation-workflows">Connected workflows</span>
            <span data-testid="hero-foundation-ai">AI across operations</span>
          </Reveal>
        </div>
        <Reveal className="hero-suite-atlas-column" delay={0.2}>
          <div className="hero-suite-atlas-stage">
            <motion.div className="hero-suite-atlas" style={{ y: reduceMotion ? 0 : atlasY, scale: reduceMotion ? 1 : atlasScale }} data-testid="hero-atlas-visual">
              <img className="hero-suite-atlas-image" src="https://static.prod-images.emergentagent.com/jobs/db200477-48fb-4087-94b1-eaecfe8c6dd7/images/fe6e10b774bb46e4b510370e33a13ee34c5d0203c3e4f2a08a46bb54ef53e016.jpeg" alt="Suvera Intelligence Platform connecting six operational product modules" />
              <div className="hero-suite-atlas-tint" aria-hidden="true" />
              <div className="hero-suite-coordinate coordinate--one" aria-hidden="true">X 042 / Y 018</div>
              <div className="hero-suite-coordinate coordinate--two" aria-hidden="true">SUVERA / 06 MODULES</div>
              <svg className="hero-suite-connectors" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true" data-testid="hero-connector-line">
                <defs><linearGradient id="suite-connector-gradient" x1="0" y1="0" x2="1" y2="0"><stop offset="0%" stopColor="#ED1C24" /><stop offset="34%" stopColor="#F2AD21" /><stop offset="66%" stopColor="#197EBD" /><stop offset="100%" stopColor="#119F4A" /></linearGradient></defs>
                {suiteProducts.map((product) => {
                  const isActive = !selectedProduct || selectedProduct === product.id;
                  return <motion.path key={product.id} d={product.path} fill="none" stroke={product.accent} strokeWidth={selectedProduct === product.id ? "1.15" : "0.55"} strokeLinecap="round" strokeDasharray={selectedProduct === product.id ? "0.8 0.2" : "0.18 0.82"} initial={false} animate={{ opacity: isActive ? 0.86 : 0.14 }} transition={{ duration: 0.3, ease: EASE }} />;
                })}
                <motion.circle className="suite-connector-pulse" r="1.1" fill="url(#suite-connector-gradient)" initial={false} animate={{ cx: activeProduct ? activeProduct.point[0] : 50, cy: activeProduct ? activeProduct.point[1] : 50, opacity: reduceMotion ? 0 : 1 }} transition={{ duration: reduceMotion ? 0.2 : 0.65, ease: EASE }} />
              </svg>
              <AnimatePresence mode="sync" initial={false}>
                <motion.div key={activeProduct ? activeProduct.id : "platform"} className="hero-suite-core" initial={reduceMotion ? { opacity: 1 } : { opacity: 0, y: 10, filter: "blur(3px)" }} animate={{ opacity: 1, y: 0, filter: "blur(0px)" }} exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -8, filter: "blur(3px)" }} transition={{ duration: reduceMotion ? 0.2 : 0.42, ease: EASE }}>
                  <span>{activeProduct ? activeProduct.category : "Shared intelligence layer"}</span>
                  <strong>{activeProduct ? activeProduct.name : "SUVERA"}</strong>
                  <small>{activeProduct ? activeProduct.blurb : "One platform / six modules"}</small>
                  <i className="hero-suite-core-line" />
                </motion.div>
              </AnimatePresence>
              {suiteProducts.map((product) => {
                const isActive = !selectedProduct || selectedProduct === product.id;
                return <button key={product.id} type="button" className={`hero-suite-node ${isActive ? "is-active" : "is-dimmed"}`} style={{ "--node-accent": product.accent, "--node-left": `${product.left}%`, "--node-top": `${product.top}%` }} onMouseEnter={() => setSelectedProduct(product.id)} onFocus={() => setSelectedProduct(product.id)} onClick={() => setSelectedProduct(product.id)} aria-pressed={selectedProduct === product.id} data-testid={`tile-${product.id}`}><span className="hero-suite-node-top"><i /> {product.shortName}</span><strong>{product.name}</strong><small>{product.category}</small></button>;
              })}
              <button className="hero-suite-reset" type="button" onClick={() => setSelectedProduct(null)} aria-label="Show all Suvera modules" data-testid="hero-suite-reset">All modules <span>↗</span></button>
            </motion.div>
            <div className="hero-suite-caption"><span>Hover or tap a module</span><span>Shared data / workflows / intelligence</span></div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}


const storyStages = [
  {
    step: "01",
    title: "Raw operational signals",
    subtitle: "Energy, vehicles, cameras, documents, and workflows enter one field.",
    accent: "#ED1C24",
    nodes: ["Energy data", "Weighbridge", "Camera feed", "Documents"],
  },
  {
    step: "02",
    title: "Organised intelligence",
    subtitle: "Signals become searchable context, events, and routes.",
    accent: "#F2AD21",
    nodes: ["Context", "Event stream", "Document logic", "Workflow rules"],
  },
  {
    step: "03",
    title: "The Suvera intelligence core",
    subtitle: "AI finds what matters and keeps systems connected to action.",
    accent: "#197EBD",
    nodes: ["AI", "Computer vision", "Automation", "Analytics"],
  },
  {
    step: "04",
    title: "Action in the real world",
    subtitle: "Visibility, decisions, and business workflows move together.",
    accent: "#119F4A",
    nodes: ["Insight", "Action", "Visibility", "Continuous improvement"],
  },
];

function PinnedStorySection() {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end end"] });
  const stageProgress = useTransform(scrollYProgress, [0, 0.26, 0.52, 0.78, 1], [0, 0, 1, 2, 3]);
  const storyY = useTransform(scrollYProgress, [0, 1], [26, -42]);
  const storyScale = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0.94, 1, 1.03, 0.97]);
  const [activeStage, setActiveStage] = useState(0);
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    if (reduceMotion) return undefined;
    return stageProgress.on("change", (value) => setActiveStage(Math.min(3, Math.max(0, Math.round(value)))));
  }, [reduceMotion, stageProgress]);

  const stage = storyStages[activeStage];
  return (
    <section className="pinned-story-section" ref={sectionRef} data-testid="pinned-story-section">
      <div className="pinned-story-sticky">
        <div className="container pinned-story-layout">
          <div className="pinned-story-copy">
            <span className="section-kicker">03 / The Suvera engine</span>
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={stage.step}
                className="story-copy-stage"
                initial={reduceMotion ? { opacity: 1, y: 0, filter: "blur(0px)" } : { opacity: 0, y: 12, filter: "blur(4px)" }}
                animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                exit={reduceMotion ? { opacity: 0, y: 0, filter: "blur(0px)" } : { opacity: 0, y: -10, filter: "blur(4px)" }}
                transition={{ duration: reduceMotion ? 0.2 : 0.42, ease: EASE }}
              >
                <div className="story-step-index"><strong>{stage.step}</strong><span>/ 04</span></div>
                <h2 data-testid={`story-stage-${stage.step}`}>{stage.title}</h2>
                <p>{stage.subtitle}</p>
              </motion.div>
            </AnimatePresence>
            <div className="story-timeline" aria-label="Suvera intelligence stages" data-testid="story-timeline-track">
              <div className="story-timeline-rail" aria-hidden="true">
                <motion.div className="story-timeline-active-segment" data-testid="story-timeline-active-segment" animate={{ width: `${(activeStage + 1) * 25}%` }} transition={{ duration: reduceMotion ? 0.2 : 0.55, ease: EASE }}>
                  <motion.span
                    key={stage.step}
                    className="story-timeline-pulse"
                    data-testid="story-timeline-pulse"
                    style={{ backgroundColor: stage.accent }}
                    animate={reduceMotion ? { opacity: 0 } : { x: ["0%", "100%"], opacity: [0, 1, 0] }}
                    transition={reduceMotion ? { duration: 0.2 } : { duration: 2.2, ease: "linear", repeat: Infinity }}
                  />
                </motion.div>
              </div>
              <div className="story-timeline-steps">
                {storyStages.map((item, index) => (
                  <div className={`story-timeline-step ${index === activeStage ? "is-active" : ""}`} key={item.step} data-testid={`story-timeline-${item.step}`}>
                    <span>{item.step}</span><i style={{ backgroundColor: index <= activeStage ? item.accent : undefined }} />
                  </div>
                ))}
              </div>
            </div>
            <a className="button button--navy story-cta" href="#contact" data-testid="story-cta">Start a conversation <ArrowUpRight size={17} aria-hidden="true" /></a>
          </div>
          <motion.div className="story-visual" style={{ y: reduceMotion ? 0 : storyY, scale: reduceMotion ? 1 : storyScale }} data-testid="story-architecture-diagram">
            <div className="story-frame">
              <div className="story-frame-header"><span><i className="story-live-dot" /> SUVERA / INTELLIGENCE ENGINE</span><span>SCROLL-LINKED SYSTEM MAP</span></div>
              <div className="story-frame-body">
                <svg className="story-connector" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true" data-testid="story-architecture-connector">
                  <defs><linearGradient id="story-connector-gradient" x1="0" y1="0" x2="1" y2="0"><stop offset="0%" stopColor="#ED1C24" /><stop offset="33%" stopColor="#F2AD21" /><stop offset="66%" stopColor="#197EBD" /><stop offset="100%" stopColor="#119F4A" /></linearGradient></defs>
                  <motion.path d="M 4 50 C 25 50 35 50 50 50 C 65 50 75 50 96 50" pathLength={1} fill="none" stroke="url(#story-connector-gradient)" strokeWidth="1.2" strokeLinecap="round" initial={false} animate={{ pathLength: reduceMotion ? 1 : (activeStage + 1) / 4, opacity: reduceMotion ? 0.42 : 1 }} transition={{ duration: reduceMotion ? 0.2 : 0.55, ease: EASE }} />
                  <motion.path d="M 4 50 C 25 50 35 50 50 50 C 65 50 75 50 96 50" pathLength={1} fill="none" stroke={stage.accent} strokeWidth="2.4" strokeLinecap="round" strokeDasharray="0.06 0.94" initial={false} animate={reduceMotion ? { opacity: 0 } : { opacity: [0, 0.82, 0], strokeDashoffset: [0, -1] }} transition={reduceMotion ? { duration: 0.2 } : { duration: 2.2, ease: "linear", repeat: Infinity }} />
                </svg>
                <div className="story-input-rail"><span className="story-rail-label">Operational input</span>{storyStages[0].nodes.map((node) => <span className="story-node" key={node}>{node}</span>)}</div>
                <div className="story-core-panel"><div className="story-core-orbit"><i /><i /><i /><i /></div><span>Core layer</span><strong>SUVERA</strong><small>Intelligence</small><div className="story-core-line" /></div>
                <div className="story-output-rail"><span className="story-rail-label">Business action</span>{stage.nodes.map((node) => <motion.span className="story-node story-node--active" key={node} initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.35, ease: EASE }}>{node}</motion.span>)}</div>
              </div>
              <div className="story-frame-footer"><span>DATA → INTELLIGENCE → ACTION</span><span className="story-stage-state" style={{ color: stage.accent }}>{stage.step} / {stage.title}</span></div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}


function EditorialMarquee() {
  const reduceMotion = useReducedMotion();
  const items = ["DATA", "INTELLIGENCE", "ACTION", "PRODUCT ENGINEERING", "AI CAPABILITIES", "REAL-WORLD SYSTEMS"];
  const loop = [...items, ...items];
  return (
    <div className="marquee-viewport" data-testid="editorial-marquee">
      <motion.div
        className="marquee-track"
        animate={reduceMotion ? { x: 0 } : { x: ["0%", "-50%"] }}
        transition={reduceMotion ? { duration: 0 } : { duration: 28, ease: "linear", repeat: Infinity }}
      >
        {loop.map((item, index) => (
          <span key={`${item}-${index}`}>
            {item} <i />
          </span>
        ))}
      </motion.div>
    </div>
  );
}

function IntroSection() {
  return (
    <section className="intro-section section" data-testid="intro-section">
      <div className="container intro-layout">
        <Reveal className="section-kicker"><span>01 / The Suvera approach</span></Reveal>
        <Reveal className="intro-copy" delay={0.1}>
          <h2>Technology should <em>understand</em> your business.</h2>
          <p>
            Modern businesses generate operational data across energy, vehicles, documents, people, cameras, workflows, and transactions. But data alone does not create value.
          </p>
          <p className="intro-emphasis">Intelligence does.</p>
          <p>
            Suvera Technology builds software and AI systems that transform operational data into visibility, automation, and action.
          </p>
        </Reveal>
        <Reveal className="intro-line-wrap" delay={0.2}><SignatureLine /></Reveal>
      </div>
    </section>
  );
}

function EmsMockup() {
  return (
    <div className="mockup-window" data-testid="product-ems-preview">
      <div className="mockup-window-head"><span><i className="status-dot status-dot--green" /> SUVERA EMS</span><span className="mockup-code">LIVE / ENERGY</span></div>
      <div className="mockup-heading">Site energy flow <small>overview</small></div>
      <div className="telemetry-grid"><span>Current demand</span><b>Operational</b><span>Consumption map</span><b>Connected</b></div>
      <div className="energy-chart" aria-hidden="true"><div className="chart-axis"><i /><i /><i /><i /><i /></div><div className="energy-bars"><b style={{ height: "42%" }} /><b style={{ height: "65%" }} /><b style={{ height: "48%" }} /><b style={{ height: "78%" }} /><b style={{ height: "57%" }} /><b style={{ height: "88%" }} /><b style={{ height: "68%" }} /></div></div>
      <div className="mockup-footer-row"><span>Efficiency signal</span><span className="signal-positive">Active <i /></span></div>
    </div>
  );
}

function WeighMockup() {
  return (
    <div className="mockup-window mockup-window--blue" data-testid="product-weighguard-preview">
      <div className="mockup-window-head"><span><i className="status-dot status-dot--blue" /> WEIGHGUARD</span><span className="mockup-code">YARD / 04</span></div>
      <div className="weighguard-visual"><div className="vehicle-mark">TRUCK</div><div className="scale-line"><i /><i /><i /></div><div className="scale-base" /></div>
      <div className="transaction-list"><div><span>Inbound vehicle</span><b>Ready</b></div><div><span>Material trace</span><b>Verified</b></div><div><span>Transaction flow</span><b>Synced</b></div></div>
    </div>
  );
}

function VisionMockup() {
  return (
    <div className="mockup-window mockup-window--red" data-testid="product-bandobast-ai-preview">
      <div className="mockup-window-head"><span><i className="status-dot status-dot--red" /> BANDOBAST AI</span><span className="mockup-code">VISION / LIVE</span></div>
      <div className="camera-grid"><div className="camera-feed"><span>CAM 01</span><i className="vision-box vision-box--one" /><i className="vision-box vision-box--two" /></div><div className="camera-feed camera-feed--muted"><span>CAM 02</span><i className="vision-box vision-box--three" /></div></div>
      <div className="alert-row"><span><i className="status-dot status-dot--red" /> Detection event</span><b>Actionable alert</b></div>
    </div>
  );
}

function DmsMockup() {
  return (
    <div className="mockup-window mockup-window--blue" data-testid="product-dms-preview">
      <div className="mockup-window-head"><span><i className="status-dot status-dot--blue" /> SUVERA DMS</span><span className="mockup-code">DOCUMENTS</span></div>
      <div className="dms-search">Search across your workspace <span>⌕</span></div>
      <div className="document-list"><div><span className="file-icon">PDF</span><span>Operations handbook <small>Updated today</small></span><b>Open</b></div><div><span className="file-icon file-icon--blue">DOC</span><span>Process workflow <small>Version controlled</small></span><b>Review</b></div><div><span className="file-icon file-icon--yellow">XLS</span><span>Site register <small>Access managed</small></span><b>Open</b></div></div>
    </div>
  );
}

function FlowMockup() {
  return (
    <div className="mockup-window mockup-window--yellow" data-testid="product-flow-preview">
      <div className="mockup-window-head"><span><i className="status-dot status-dot--yellow" /> SUVERA FLOW</span><span className="mockup-code">WORKFLOW / ACTIVE</span></div>
      <div className="flow-steps"><div className="flow-step flow-step--complete"><i>01</i><span>Visitor request</span></div><div className="flow-connector" /><div className="flow-step"><i>02</i><span>Host approval</span></div><div className="flow-connector" /><div className="flow-step"><i>03</i><span>Arrival action</span></div></div>
      <div className="mockup-footer-row"><span>Workflow state</span><span className="signal-positive signal-positive--yellow">Moving <i /></span></div>
    </div>
  );
}

function AiMockup() {
  return (
    <div className="mockup-window mockup-window--yellow" data-testid="product-ai-preview">
      <div className="mockup-window-head"><span><i className="status-dot status-dot--yellow" /> AI CAPABILITIES</span><span className="mockup-code">INTELLIGENCE</span></div>
      <div className="ai-node-map"><span className="ai-node ai-node--main">AI</span><span className="ai-node ai-node--one">SEE</span><span className="ai-node ai-node--two">UNDERSTAND</span><span className="ai-node ai-node--three">ACT</span><span className="ai-node ai-node--four">IMPROVE</span></div>
      <div className="mockup-footer-row"><span>Data into action</span><span className="signal-positive signal-positive--yellow">Ready <i /></span></div>
    </div>
  );
}

function ProductMockup({ kind }) {
  if (kind === "ems") return <EmsMockup />;
  if (kind === "weighguard") return <WeighMockup />;
  if (kind === "bandobast-ai") return <VisionMockup />;
  if (kind === "dms") return <DmsMockup />;
  if (kind === "flow") return <FlowMockup />;
  return <AiMockup />;
}

function ProductCard({ product, index }) {
  return (
    <Reveal className={`product-card-wrap product-card-wrap--${product.layout}`} delay={index * 0.06}>
      <motion.article className="product-card" style={{ "--product-accent": product.accent }} whileHover={{ y: -5 }} data-testid={`product-${product.slug}-card`}>
        <div className="product-card-topline"><span className="product-index">0{index + 1}</span><span className="product-category">{product.category}</span></div>
        <div className="product-card-heading"><h3>{product.name}</h3><p>{product.description}</p></div>
        <ProductMockup kind={product.slug} />
        <a className="product-explore" href={product.slug === "flow" ? "/products/flow" : "#contact"} data-testid={`product-${product.slug}-link`}>
          Explore <ArrowUpRight size={17} aria-hidden="true" />
        </a>
      </motion.article>
    </Reveal>
  );
}

function ProductsSection() {
  return (
    <section className="products-section section" id="products" data-testid="products-section">
      <div className="container">
        <Reveal className="section-heading-row"><div><span className="section-kicker">02 / Our products</span><h2>Built for real-world<br /><em>business operations.</em></h2></div><p>Specialised digital products around problems businesses actually face.</p></Reveal>
        <div className="products-grid">{products.map((product, index) => <ProductCard key={product.slug} product={product} index={index} />)}</div>
      </div>
    </section>
  );
}

function AiSection() {
  const stages = [
    ["SEE", "Computer vision", "AI video analytics, object and event detection.", "#ED1C24"],
    ["UNDERSTAND", "AI & data intelligence", "Document intelligence, pattern recognition, operational context.", "#F2AD21"],
    ["ACT", "Intelligent automation", "Alerts, decision support, and workflows that move.", "#197EBD"],
    ["IMPROVE", "Continuous intelligence", "Feedback loops that help systems become more useful over time.", "#119F4A"],
  ];
  return (
    <section className="ai-section section section--dark" id="ai" data-testid="ai-section">
      <div className="container">
        <div className="ai-header-grid">
          <Reveal><span className="section-kicker section-kicker--light">03 / AI at the core</span><h2>Intelligence that turns<br /><em>data into action.</em></h2></Reveal>
          <Reveal delay={0.12}><p className="dark-lead">Suvera combines software engineering with artificial intelligence to create systems that do not just store information — they understand it.</p><SignatureLine dark /></Reveal>
        </div>
        <div className="ai-stages">{stages.map(([label, title, copy, accent], index) => <Reveal key={label} delay={index * 0.08}><motion.article className="ai-stage" whileHover={{ y: -4 }} data-testid={`ai-stage-${label.toLowerCase()}`}><span className="stage-number">0{index + 1}</span><span className="stage-label" style={{ color: accent }}>{label}</span><h3>{title}</h3><p>{copy}</p><ArrowUpRight size={18} aria-hidden="true" /></motion.article></Reveal>)}</div>
        <Reveal className="architecture-panel" delay={0.16}>
          <div className="architecture-heading"><span className="mono-label">Suvera intelligence architecture</span><span className="architecture-status"><i /> Connected systems</span></div>
          <div className="architecture-flow" data-testid="ai-architecture-flow">
            <div className="architecture-column"><span className="architecture-column-label">Business inputs</span><div className="architecture-nodes"><span>Energy data</span><span>Vehicles</span><span>Cameras</span><span>Documents</span><span>Workflows</span></div></div>
            <div className="architecture-core"><div><span className="core-ring" /><strong>SUVERA</strong><small>Intelligence layer</small></div></div>
            <div className="architecture-column"><span className="architecture-column-label">Business outcomes</span><div className="architecture-nodes"><span>Visibility</span><span>Efficiency</span><span>Security</span><span>Automation</span><span>Better decisions</span></div></div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function ManifestoSection() {
  return (
    <section className="manifesto-section section" id="company" data-testid="manifesto-section">
      <div className="container manifesto-layout">
        <Reveal className="manifesto-intro"><span className="section-kicker">04 / Why Suvera</span><h2>We do not just build software.<br /><em>We engineer intelligence.</em></h2><p>Every product starts with the operational reality behind the interface.</p></Reveal>
        <div className="manifesto-chapters">{chapters.map(([number, title, copy], index) => <Reveal key={number} delay={index * 0.05}><article className="manifesto-chapter" data-testid={`manifesto-chapter-${number}`}><span className="chapter-number">{number}</span><h3>{title}</h3><p>{copy}</p><ArrowUpRight size={18} aria-hidden="true" /></article></Reveal>)}</div>
      </div>
    </section>
  );
}

function SolutionsSection() {
  return (
    <section className="solutions-section section section--ice" id="solutions" data-testid="solutions-section">
      <div className="container"><Reveal className="section-heading-row section-heading-row--solutions"><div><span className="section-kicker">05 / Solutions</span><h2>From operational complexity<br /><em>to digital intelligence.</em></h2></div><p>Connect the systems, people, and signals that keep your business moving.</p></Reveal>
        <div className="solutions-grid">{solutions.map(([title, copy, accent], index) => <Reveal key={title} delay={index * 0.05}><article className="solution-card" style={{ "--solution-accent": accent }} data-testid={`solution-${index + 1}`}><span className="solution-index">0{index + 1}</span><div><h3>{title}</h3><p>{copy}</p></div><ArrowUpRight size={18} aria-hidden="true" /></article></Reveal>)}</div>
      </div>
    </section>
  );
}

function EcosystemSection() {
  const leftNodes = [["Suvera EMS", "Energy", "#119F4A"], ["WeighGuard", "Weighbridge", "#197EBD"], ["Bandobast AI", "Vision", "#ED1C24"]];
  const rightNodes = [["Suvera DMS", "Documents", "#197EBD"], ["Suvera Flow", "Workflows", "#F2AD21"], ["AI Capabilities", "Intelligence", "#F2AD21"]];
  const node = ([title, detail, accent]) => <a className="ecosystem-node" href={title === "Suvera Flow" ? "/products/flow" : "#products"} style={{ "--node-accent": accent }} key={title} data-testid={`ecosystem-${title.toLowerCase().replaceAll(" ", "-")}-link`}><span className="node-dot" /><span><strong>{title}</strong><small>{detail}</small></span><ArrowUpRight size={15} aria-hidden="true" /></a>;
  return (
    <section className="ecosystem-section section" id="ecosystem" data-testid="ecosystem-section">
      <div className="container"><Reveal className="section-heading-row"><div><span className="section-kicker">06 / Product ecosystem</span><h2>One technology ecosystem.<br /><em>Multiple business challenges.</em></h2></div><p>A connected intelligence layer for the systems that make modern operations work.</p></Reveal>
        <Reveal className="ecosystem-shell" delay={0.1}>
          <div className="ecosystem-canvas"><svg className="ecosystem-connectors" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true"><path d="M18 20 C35 20 36 42 49 50" /><path d="M18 50 C35 50 38 50 49 50" /><path d="M18 80 C35 80 38 58 49 50" /><path d="M82 20 C65 20 64 42 51 50" /><path d="M82 50 C65 50 62 50 51 50" /><path d="M82 80 C65 80 62 58 51 50" /></svg><div className="ecosystem-column">{leftNodes.map(node)}</div><div className="ecosystem-core"><span className="core-kicker">Central layer</span><div className="ecosystem-core-mark"><i /><i /><i /><i /></div><strong>SUVERA</strong><span>INTELLIGENCE LAYER</span><SignatureLine /></div><div className="ecosystem-column">{rightNodes.map(node)}</div></div>
        </Reveal>
      </div>
    </section>
  );
}

function ApproachSection() {
  const steps = [["01", "Discover", "Understand the business."], ["02", "Define", "Identify the right solution."], ["03", "Architect", "Design the technology foundation."], ["04", "Engineer", "Build the product."], ["05", "Integrate", "Connect systems and infrastructure."], ["06", "Deploy", "Take the solution into production."], ["07", "Evolve", "Continuously improve."]];
  return (
    <section className="approach-section section section--ice" id="approach" data-testid="approach-section"><div className="container"><Reveal className="section-heading-row"><div><span className="section-kicker">07 / Our approach</span><h2>From complexity<br /><em>to clarity.</em></h2></div><p>A disciplined path from the first question to a useful system in the real world.</p></Reveal><div className="approach-grid">{steps.map(([number, title, copy], index) => <Reveal key={number} delay={index * 0.04}><article className="approach-step" data-testid={`approach-step-${number}`}><span>{number}</span><h3>{title}</h3><p>{copy}</p></article></Reveal>)}</div></div></section>
  );
}

function EnterpriseSection() {
  const capabilities = [["SECURE", "Role-based access and controlled information."], ["SCALABLE", "Designed for users, locations, devices, and data."], ["INTEGRATED", "Connect with existing systems and infrastructure."], ["FLEXIBLE", "Cloud, on-premise, or hybrid deployment."], ["OBSERVABLE", "Reporting, analytics, and operational visibility."]];
  return (
    <section className="enterprise-section section section--dark" id="enterprise" data-testid="enterprise-section"><div className="container"><div className="enterprise-heading"><Reveal><span className="section-kicker section-kicker--light">08 / Enterprise readiness</span><h2>Built for<br /><em>production.</em></h2></Reveal><Reveal delay={0.1}><p>Your business cannot run on prototypes. Suvera products are engineered around the reliability, security, scalability, and operational requirements expected from production environments.</p></Reveal></div><div className="enterprise-list">{capabilities.map(([title, copy], index) => <Reveal key={title} delay={index * 0.05}><article data-testid={`enterprise-${title.toLowerCase()}`}><span>0{index + 1}</span><h3>{title}</h3><p>{copy}</p></article></Reveal>)}</div><SignatureLine dark /></div></section>
  );
}

function IndustriesSection() {
  return (
    <section className="industries-section section" id="industries" data-testid="industries-section"><div className="container"><Reveal className="section-heading-row"><div><span className="section-kicker">09 / Industries</span><h2>Technology for the environments<br /><em>where business happens.</em></h2></div></Reveal><div className="industries-grid">{industries.map(([title, copy], index) => <Reveal key={title} delay={index * 0.04}><article className="industry-card" data-testid={`industry-${index + 1}`}><span>0{index + 1}</span><h3>{title}</h3><p>{copy}</p></article></Reveal>)}</div></div></section>
  );
}

function AboutSection() {
  return (
    <section className="about-section section section--ice" id="about" data-testid="about-section"><div className="container about-layout"><Reveal className="about-brand-panel"><span className="mono-label">The Suvera mark</span><Logo variant="color" testId="about-color-logo" /><span className="about-brand-note">Engineering intelligent digital products, powered by AI.</span></Reveal><Reveal className="about-copy" delay={0.12}><span className="section-kicker">10 / About Suvera</span><h2>We build technology<br /><em>for the real world.</em></h2><p>Suvera Technology is an AI and software product company focused on solving complex operational challenges through intelligent digital systems.</p><p>We combine software engineering, artificial intelligence, automation, data, and operational understanding to create technology that solves real business challenges.</p><div className="about-mantra"><span>Understand the problem.</span><span>Engineer the solution.</span><span>Create measurable value.</span></div></Reveal></div></section>
  );
}

function VisionSection() {
  return (
    <section className="vision-section section section--dark" id="vision" data-testid="vision-section"><div className="container vision-inner"><Reveal><span className="section-kicker section-kicker--light">11 / The vision</span><h2>A world where every business operation can become <em>intelligent.</em></h2><p>Software should do more than digitise existing processes. It should help businesses understand, predict, automate, and improve the way they operate.</p><strong>That is the future we are building.</strong><SignatureLine dark /></Reveal></div></section>
  );
}

const initialForm = { name: "", company: "", work_email: "", phone: "", requirement: "", message: "" };

function ContactForm() {
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState("idle");
  const update = (field) => (event) => setForm((current) => ({ ...current, [field]: event.target.value }));
  const submit = async (event) => {
    event.preventDefault();
    setStatus("sending");
    try {
      const response = await fetch(`${API_URL}/contact`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(form) });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.detail || "Unable to send enquiry");
      setForm(initialForm);
      setStatus("success");
    } catch (error) {
      setStatus("error");
    }
  };
  return (
    <form className="contact-form" onSubmit={submit} data-testid="contact-form">
      <div className="form-grid">
        <label className="form-field"><span>Name</span><input name="name" value={form.name} onChange={update("name")} placeholder="Your name" required data-testid="contact-name-input" /></label>
        <label className="form-field"><span>Company</span><input name="company" value={form.company} onChange={update("company")} placeholder="Your company" required data-testid="contact-company-input" /></label>
        <label className="form-field"><span>Work email</span><input type="email" name="work_email" value={form.work_email} onChange={update("work_email")} placeholder="you@company.com" required data-testid="contact-email-input" /></label>
        <label className="form-field"><span>Phone <small>Optional</small></span><input type="tel" name="phone" value={form.phone} onChange={update("phone")} placeholder="+00 000 000 000" data-testid="contact-phone-input" /></label>
        <label className="form-field form-field--full"><span>Requirement</span><select name="requirement" value={form.requirement} onChange={update("requirement")} required data-testid="contact-requirement-select"><option value="">Choose a requirement</option><option>Product Information</option><option>AI Solution</option><option>Custom Software</option><option>Digital Transformation</option><option>System Integration</option><option>Other</option></select></label>
        <label className="form-field form-field--full"><span>Message</span><textarea name="message" value={form.message} onChange={update("message")} placeholder="Tell us what you are trying to build or improve" rows="5" required data-testid="contact-message-input" /></label>
      </div>
      <div className="form-submit-row"><button className="button button--navy" type="submit" disabled={status === "sending"} data-testid="contact-submit-btn">{status === "sending" ? "Sending..." : "Send enquiry"} <ArrowUpRight size={17} aria-hidden="true" /></button><span className="form-privacy">Your enquiry goes directly to Suvera Technology.</span></div>
      {status === "success" && <div className="form-status form-status--success" role="status" data-testid="contact-success"><Check size={17} /> Thanks — your enquiry is on its way to the Suvera team.</div>}
      {status === "error" && <div className="form-status form-status--error" role="alert" data-testid="contact-error">We could not send that enquiry right now. Please try again shortly.</div>}
    </form>
  );
}

function ContactSection() {
  return (
    <section className="contact-section section section--dark" id="contact" data-testid="contact-section"><div className="container contact-layout"><Reveal className="contact-copy"><span className="section-kicker section-kicker--light">12 / Start a conversation</span><h2>Have a complex<br /><em>problem?</em></h2><p className="contact-title-line">Let&apos;s engineer the solution.</p><p>Whether you are looking to introduce AI, automate an operation, modernise an existing system, or build a new digital product — let&apos;s talk.</p><SignatureLine dark /></Reveal><Reveal className="contact-form-wrap" delay={0.12}><ContactForm /></Reveal></div></section>
  );
}

function Footer() {
  return (
    <footer className="site-footer" data-testid="site-footer"><div className="container"><div className="footer-top"><div className="footer-brand"><Logo variant="color" testId="footer-logo" /><p>Engineering Intelligent Digital Products, Powered by AI.</p><a href="mailto:info@suveragroups.com" data-testid="footer-email-link">info@suveragroups.com <ArrowUpRight size={15} /></a></div><div className="footer-column"><span>Products</span><a href="#products" data-testid="footer-ems-link">Suvera EMS</a><a href="#products" data-testid="footer-weighguard-link">WeighGuard</a><a href="#products" data-testid="footer-bandobast-link">Bandobast AI</a><a href="#products" data-testid="footer-dms-link">Suvera DMS</a><a href="/products/flow" data-testid="footer-flow-link">Suvera Flow</a><a href="#ai" data-testid="footer-ai-link">AI Capabilities</a></div><div className="footer-column"><span>Solutions</span><a href="#solutions" data-testid="footer-transformation-link">Digital transformation</a><a href="#solutions" data-testid="footer-automation-link">AI &amp; automation</a><a href="#solutions" data-testid="footer-vision-link">Computer vision</a><a href="#solutions" data-testid="footer-enterprise-link">Enterprise software</a><a href="#solutions" data-testid="footer-integration-link">System integration</a></div><div className="footer-column"><span>Company</span><a href="#about" data-testid="footer-about-link">About</a><a href="#approach" data-testid="footer-approach-link">Our approach</a><a href="#industries" data-testid="footer-industries-link">Industries</a><a href="#contact" data-testid="footer-contact-link">Contact</a><span className="footer-muted">LinkedIn / coming soon</span></div></div><div className="footer-bottom"><span>© 2026 Suvera Technology. All rights reserved.</span><span>Intelligence line / data → intelligence → action</span></div></div></footer>
  );
}

function HomePage() {
  return (
    <div className="site-shell">
      <SmoothScroll />
      <a className="skip-link" href="#main-content" data-testid="skip-to-content">Skip to content</a>
      <Header />
      <main id="main-content">
        <HeroEditorial />
        <EditorialMarquee />
        <PinnedStorySection />
        <IntroSection />
        <ProductsSection />
        <AiSection />
        <ManifestoSection />
        <SolutionsSection />
        <EcosystemSection />
        <ApproachSection />
        <EnterpriseSection />
        <IndustriesSection />
        <AboutSection />
        <VisionSection />
        <ContactSection />
      </main>
      <Footer />
    </div>
  );
}

export default function App() {
  const path = window.location.pathname.replace(/\/$/, "");
  if (path === "/products/flow") return <FlowPage />;
  return <HomePage />;
}
